import { AnchorButton } from '@askable/ui/core/button';
import { useTranslation } from 'react-i18next';

import { LockedPopover, useLockedPopover } from 'components/common/LockedPopover';

import type { LockedPopoverSide } from 'components/common/LockedPopover';
import type { ReactNode } from 'react';

const HELP_ARTICLE_URL = 'https://help.askable.com/en/articles/4275592';

type Props = {
  children: ReactNode;
  description: string;
  side?: LockedPopoverSide;
};

/**
 * Wraps a (disabled) trigger and surfaces a guest explanation popover.
 *
 * Provides the `group relative` wrapper and click-to-toggle state, then renders
 * the shared {@link LockedPopover} panel as a sibling of the trigger. When
 * rendered inside a {@link LockedPopoverGroup}, opening one popover closes the
 * others; entering a different item dismisses any clicked-open panel.
 */
export const GuestLockedPopover = ({ children, description, side = 'right' }: Props) => {
  const { t } = useTranslation();
  const { isOpen, toggle, close } = useLockedPopover();

  return (
    // Capture the click before the trigger can stopPropagation so click-to-toggle stays robust.
    <div className="group relative" onClickCapture={toggle} onMouseEnter={close}>
      {children}
      <LockedPopover isOpen={isOpen} side={side} className="w-60 p-3">
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex flex-col gap-1">
            <div className="font-semibold leading-snug">{t('sections.createStudy.guest.title')}.</div>
            <div className="text-pretty text-muted-foreground">{description}</div>
          </div>
          {/* Stop propagation so the panel's preventDefault doesn't cancel navigation. */}
          <AnchorButton
            variant="outline"
            href={HELP_ARTICLE_URL}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
          >
            {t('global.learnMore')}
          </AnchorButton>
        </div>
      </LockedPopover>
    </div>
  );
};
